/**
 * The segment bands' tones, read off the server's silhouette SVG (stegborr-segmentmodellen/18).
 *
 * <p><b>The server is the only palette.</b> `RotaryToolSilhouetteVisualizer` owns the geometry, sets
 * `data-seg` and paints one band per segment with `fill` (the one shared hue) and `fill-opacity`
 * (that band's step on the lightness ramp) as presentation attributes on the rect. The list paints
 * the SAME pair behind its row numbers, so a retune of the ramp or the hue lands in both places
 * from one edit — and a second palette in TypeScript is exactly what this module exists not to be.
 * `segment-band-tone.spec.ts` measures the two computed colours against each other.</p>
 *
 * <p><b>Why a regex and not DOMParser.</b> The markup is our own serializer's
 * (`SvgElement.Write`): a band is an attribute-only `<rect … />`, attributes are `key="value"` with
 * no quotes inside, so matching it is deterministic. Parsing the whole document into a DOM for a
 * handful of attributes would rebuild what the silhouette component already builds, once per
 * evaluation — and would tie this to a browser global, which keeps it out of the cheap unit-test
 * half.</p>
 */

/** The class the visualizer puts on every band rect (RotaryToolClass.SegmentBand). */
const BAND_CLASS = 'viz-segment-band';

/**
 * Every self-closing rect in the document — the bands, but also every hit rect and hatch tile, so
 * the per-rect work below is kept to a minimum. The four attribute patterns are compiled ONCE, here,
 * not per attribute per rect: `readSegmentTones` runs on every evaluation payload, and a drawing
 * with n segments carries a hit rect per measure on top of its n bands.
 */
const RECT = /<rect\b([^>]*)\/>/g;
const SEG_ATTR = /\sdata-seg="([^"]*)"/;
const CLASS_ATTR = /\sclass="([^"]*)"/;
const FILL_ATTR = /\sfill="([^"]*)"/;
const FILL_OPACITY_ATTR = /\sfill-opacity="([^"]*)"/;
const attr = (attrs: string, pattern: RegExp): string | undefined => pattern.exec(attrs)?.[1];

/** A hex or rgb() literal — anything else is not a colour we will put into a style attribute. */
const COLOUR = /^(#[0-9a-f]{6}|#[0-9a-f]{3}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))$/i;

/**
 * The CSS colour for one band: its fill at its opacity, as a `color-mix` over transparent so the
 * browser composites it over whatever ground the row sits on — the same thing `fill-opacity` does
 * for the rect over the drawing's page. Undefined when either half is not a value we can trust.
 */
export function bandTone(fill: string | undefined, fillOpacity: string | undefined): string | undefined {
    if (!fill || !COLOUR.test(fill.trim())) return undefined;
    const alpha = Number(fillOpacity);
    if (!Number.isFinite(alpha) || alpha < 0 || alpha > 1) return undefined;
    // Two decimals of a percent is finer than the server ever emits (SvgFormat.F → 4 decimals of
    // an opacity), so the round trip is exact for every value it can produce.
    const pct = Math.round(alpha * 10_000) / 100;
    return `color-mix(in srgb, ${fill.trim()} ${pct}%, transparent)`;
}

/**
 * Every band's tone in the SVG, keyed by its `data-seg` tag (`alias.seg0`, …, `alias.shank`).
 * An SVG without bands — a thumbnail, a bore, an older server — yields an empty map, and the list
 * paints nothing behind its rows.
 */
export function readSegmentTones(svg: string | undefined): Map<string, string> {
    const tones = new Map<string, string>();
    if (!svg) return tones;
    for (const m of svg.matchAll(RECT)) {
        const attrs = m[1];
        // data-seg FIRST: it is the attribute most rects lack (hit rects, hatch tiles, masks), so a
        // non-band falls out after one match instead of three.
        const seg = attr(attrs, SEG_ATTR);
        if (!seg) continue;
        const cls = attr(attrs, CLASS_ATTR);
        if (!cls || !cls.split(/\s+/).includes(BAND_CLASS)) continue;
        const tone = bandTone(attr(attrs, FILL_ATTR), attr(attrs, FILL_OPACITY_ATTR));
        if (tone) tones.set(seg, tone);
    }
    return tones;
}
